// Public, anonymous folder browser for the `library` block's FOLDER mode. Renders
// a slideout-style browser rooted at a PUBLIC ("shared_global") or for-sale
// folder: visitors navigate subfolders (never above the designated root — the
// server rejects any folderId not descended from rootId), see folder cover icons,
// and click a file to preview (via the shared <LibraryMediaView>) + download.
//
// Two layouts: 'grid' = large cover cards; 'browser' = compact library-panel-style
// rows (thumbnail + name) fitted to the block. For paid roots the visitor sees a
// teaser (blurred, no download) + an "Unlock for $X" button → Stripe checkout
// (sign-in forced first). In EDIT mode each folder shows a 📎 to set its cover
// icon inline (owner only — the editor IS the owner).
//
// It calls the anonymous GET /api/library/public/folders endpoint, which resolves
// the owner from the root folder itself — so this works identically in the editor
// canvas and on the live page with NO orgSlug needed (a signed-in cookie unlocks
// paid content).
import React, { useEffect, useState } from 'react';
import type { LibraryBlock } from '../../../../shared/pageBlocks';
import { useAuth } from '@/contexts/authContextCore';
import { getCsrfToken } from '@/services/csrfService';
import { resolveLibraryCoverForFolder } from '@/services/newsletterAdminService';
import { LibraryMediaView } from './LibraryMediaView';

/**
 * The app store is only needed for these admin / download actions, so it loads
 * when one runs: a visitor reading a folder on the public storefront never pulls
 * the whole app (chat, Matrix, shader engine) in.
 */
const loadAppStore = async () => (await import('@/stores/appStore')).useAppStore.getState();
const showToastLazy = (message: string, type: 'info' | 'error') => {
  void loadAppStore().then((store) => store.showToast?.(message, type)).catch(() => undefined);
};

// A locked (paid) file's thumbnail is dimmed rather than blurred: translucency
// is cheap, a CSS blur is not.
const LOCKED_THUMB_OPACITY = 0.35;

interface FolderRef { id: string; name: string }
// Per-node sale state (sale points can live at any depth now): `paid` = covered by
// a sale point, `owned` = the viewer is entitled, `salePointId` = the node whose
// purchase unlocks this (a folder bundle, or a single file's own id).
interface SaleState { paid?: boolean; owned?: boolean; priceCents?: number | null; currency?: string | null; salePointId?: string | null }
interface SubfolderRef extends FolderRef, SaleState { mediaCount?: number; coverUrl?: string | null }
interface FileRef extends SaleState { id: string; filename: string; mime: string; thumbUrl?: string | null; url: string | null; salePointKind?: 'folder' | 'media' | null }
interface ListResponse extends SaleState {
  success: boolean;
  error?: string;
  root: FolderRef;
  folder: FolderRef;
  breadcrumb: FolderRef[];
  subfolders: SubfolderRef[];
  files: FileRef[];
}

function formatPrice(cents?: number | null, currency?: string | null): string {
  const amount = (cents ?? 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

async function authedJson(url: string, body: unknown, method = 'POST'): Promise<Response> {
  const csrf = getCsrfToken();
  return fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
    body: JSON.stringify(body),
  });
}

const FolderIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

const FileIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const VideoFileIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
    <rect x="1" y="5" width="15" height="14" rx="2" />
    <path d="M23 7l-7 5 7 5V7z" />
  </svg>
);

// Icon shown for a file tile that has no thumbnail (or whose thumbnail failed
// to load) — a video icon for video files, a generic document icon otherwise.
// Never falls back to plain filename text: that's already shown as the tile's
// label, so repeating it inside the thumb box would just be visual clutter.
const fileFallbackIcon = (mime: string, size: number): React.ReactNode =>
  mime.startsWith('video/') ? <VideoFileIcon size={size} /> : <FileIcon size={size} />;

// Wraps a thumbnail <img>: on load failure (404, unsupported/corrupt format,
// etc.) swaps to `fallback` instead of the browser's native broken-image glyph.
const Thumb: React.FC<{ src: string; alt: string; style: React.CSSProperties; fallback: React.ReactNode }> = ({ src, alt, style, fallback }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (broken) return <>{fallback}</>;
  return <img src={src} alt={alt} style={style} loading="lazy" onError={() => setBroken(true)} />;
};

// Small floating paperclip (edit mode only) to set a folder's cover icon inline,
// mirroring the image-block paperclip. Sibling of the card (never nested in a
// <button>); stops propagation so it never navigates/selects.
const CoverClip: React.FC<{ busy: boolean; onClick: () => void }> = ({ busy, onClick }) => (
  // `icon-btn` opts out of the global mobile 44px min-height (index.css) so the
  // round button stays a circle on phones / portrait, not a tall oval.
  <button type="button" className="icon-btn" title="Set this folder's cover icon" disabled={busy}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      position: 'absolute', top: 4, left: 4, zIndex: 5, pointerEvents: 'auto',
      width: 24, height: 24, borderRadius: '50%', border: 'none', cursor: busy ? 'wait' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(24,24,27,0.9)', color: '#e4e4e7', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
    }}>
    {busy ? '…' : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
      </svg>
    )}
  </button>
);

// Download-whole-folder button: bottom-right on grid cards, center-right on
// compact rows. Sibling of the card (never nested in its <button>); stops
// propagation so it never navigates.
const DownloadClip: React.FC<{ busy: boolean; compact: boolean; color: string; onClick: () => void }> = ({ busy, compact, color, onClick }) => (
  // `icon-btn` opts out of the global mobile 44px min-height (index.css) so the
  // round button stays a circle on phones / portrait, not a tall oval. box-sizing
  // + flex:none keep it exactly 26×26 regardless of any inherited button padding.
  <button type="button" className="icon-btn" title="Download this whole folder (.zip)" disabled={busy}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      position: 'absolute', zIndex: 5, pointerEvents: 'auto', boxSizing: 'border-box', flex: 'none', padding: 0,
      ...(compact ? { top: '50%', right: 8, transform: 'translateY(-50%)' } : { bottom: 8, right: 8 }),
      width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: busy ? 'wait' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: color, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.45)',
    }}>
    {busy ? '…' : (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    )}
  </button>
);

export const LibraryFolderBrowser: React.FC<{ block: LibraryBlock; editing?: boolean }> = ({ block, editing }) => {
  const { user } = useAuth();
  const [folderId, setFolderId] = useState(block.folderId);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<FileRef | null>(null);
  const [buying, setBuying] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [coverBusyId, setCoverBusyId] = useState<string | null>(null);
  const [downloadBusyId, setDownloadBusyId] = useState<string | null>(null);

  useEffect(() => { setFolderId(block.folderId); }, [block.folderId]);

  useEffect(() => {
    if (!block.folderId) return;
    let alive = true;
    setLoading(true); setErr(null);
    const qs = new URLSearchParams({ rootId: block.folderId, folderId: folderId || block.folderId });
    fetch(`/api/library/public/folders?${qs.toString()}`, { credentials: 'include' })
      .then(async (r) => {
        const j = (await r.json()) as ListResponse;
        if (!r.ok || !j.success) throw new Error(j.error || `Failed to load (${r.status})`);
        return j;
      })
      .then((j) => { if (alive) setData(j); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : 'Could not load folder'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [block.folderId, folderId, reloadKey]);

  // Stripe return (webhook-miss fallback): verify the session for whichever node
  // was bought — a folder bundle (?folderPurchase=success&folderId=…) OR a single
  // file (?mediaPurchase=success&mediaId=…) at any depth — then reload + clean URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const folderId = params.get('folderPurchase') === 'success' ? params.get('folderId') : null;
    const mediaId = params.get('mediaPurchase') === 'success' ? params.get('mediaId') : null;
    if (!folderId && !mediaId) return;
    const sessionId = params.get('session_id');
    const clean = () => {
      const url = new URL(window.location.href);
      ['folderPurchase', 'folderId', 'mediaPurchase', 'mediaId', 'session_id'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.pathname + url.search);
    };
    if (!sessionId) { clean(); return; }
    const endpoint = folderId
      ? `/api/library/folders/${folderId}/verify-purchase`
      : `/api/library/media/${mediaId}/verify-purchase`;
    void authedJson(endpoint, { session_id: sessionId })
      .catch(() => null)
      .finally(() => { clean(); setReloadKey((k) => k + 1); });
    // Run once on return; the id comes from the URL, not props.
  }, []);

  // Start Stripe checkout for a sale point — a folder bundle or a single file.
  const handleBuyNode = async (kind: 'folder' | 'media', id: string) => {
    if (!user) { window.dispatchEvent(new Event('open-signup')); return; }
    setBuying(true); setErr(null);
    try {
      const returnUrl = window.location.pathname + window.location.search;
      const base = kind === 'media' ? 'media' : 'folders';
      const res = await authedJson(`/api/library/${base}/${id}/purchase-checkout`, { returnUrl });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Could not start checkout');
      if (j.alreadyOwned) { setReloadKey((k) => k + 1); return; }
      if (j.url) { window.location.href = j.url; return; }
      throw new Error('Checkout did not return a URL');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start checkout');
    } finally {
      setBuying(false);
    }
  };
  // The CURRENT folder's covering sale point (always a folder) — the banner CTA.
  const handleBuy = () => handleBuyNode('folder', data?.salePointId || block.folderId);

  // Edit mode: set a folder's cover icon inline (image, or a video's thumbnail).
  const setCoverFor = async (fid: string) => {
    setCoverBusyId(fid);
    try {
      const item = await (await loadAppStore()).beginLibraryPick({});
      if (!item) return;
      const resolved = await resolveLibraryCoverForFolder(item as never);
      const res = await authedJson('/api/library/folder-covers', { folderId: fid, src: resolved.src }, 'PUT');
      if (!res.ok) throw new Error('Could not save cover');
      showToastLazy('Folder cover set', 'info');
      // Refresh the Library panel's folder thumbnails too (unified look).
      window.dispatchEvent(new CustomEvent('library-folders:changed', { detail: { op: 'cover', id: fid } }));
      setReloadKey((k) => k + 1);
    } catch (e) {
      showToastLazy(e instanceof Error ? e.message : 'Could not set cover', 'error');
    } finally {
      setCoverBusyId(null);
    }
  };

  if (!block.folderId) return null;

  const locked = !!data?.paid && !data?.owned;
  const columns = Math.max(1, Math.min(6, block.columns || 3));
  const compact = block.layout === 'browser';
  const dlColor = block.dlButtonColor || 'rgba(37,99,235,0.92)';
  const breadcrumbSize = block.breadcrumbFontSize || 12;

  const onFileClick = (f: FileRef) => {
    // Locked file → checkout its OWN sale point (a single-file sale) or the
    // covering folder bundle. Otherwise open the lightbox.
    if (f.paid && !f.owned) {
      if (f.salePointKind === 'media') void handleBuyNode('media', f.salePointId || f.id);
      else void handleBuy();
      return;
    }
    if (locked || !f.url) { void handleBuy(); return; }
    setActive(f);
  };

  // Download a whole (sub)folder as a .zip: walk the public endpoint to collect
  // every file (with its relative path), fetch each, and zip client-side. ALL
  // downloads require sign-in; a locked (paid, unowned) folder then routes to
  // checkout. File byte fetches use the DEFAULT ('same-origin') credentials so the
  // cookie reaches the gated droplet URL but is NOT sent on its cross-origin CDN
  // redirect (wildcard CORS).
  const collectFiles = async (rootId: string, fId: string, prefix: string, out: Array<{ path: string; url: string }>) => {
    const qs = new URLSearchParams({ rootId, folderId: fId });
    const r = await fetch(`/api/library/public/folders?${qs.toString()}`, { credentials: 'include' });
    const j = (await r.json()) as ListResponse;
    if (!j.success) throw new Error(j.error || 'Failed to load folder');
    for (const f of j.files) { if (f.url) out.push({ path: prefix + f.filename, url: f.url }); }
    for (const sub of j.subfolders) { await collectFiles(rootId, sub.id, `${prefix}${sub.name}/`, out); }
  };

  const downloadFolder = async (folder: SubfolderRef) => {
    if (!user) { window.dispatchEvent(new Event('open-signup')); return; }
    if (locked) { void handleBuy(); return; }
    setDownloadBusyId(folder.id);
    try {
      const out: Array<{ path: string; url: string }> = [];
      await collectFiles(block.folderId, folder.id, '', out);
      if (out.length === 0) { showToastLazy('This folder has no downloadable files.', 'info'); return; }
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      for (const item of out) {
        const res = await fetch(item.url); // default same-origin (see note above)
        if (res.ok) zip.file(item.path, await res.blob());
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = `${folder.name}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      showToastLazy(e instanceof Error ? e.message : 'Download failed', 'error');
    } finally {
      setDownloadBusyId(null);
    }
  };

  // ── Grid layout (cover cards) ──────────────────────────────────────────────
  const minCol = Math.max(120, Math.floor(900 / columns));
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(min(${minCol}px, 100%), 1fr))`,
    gap: 16,
  };
  const card = (key: string, label: string, sub: string | undefined, thumb: string | null | undefined, fallback: React.ReactNode, onClick: () => void, opts: { lock?: boolean; clip?: string; dl?: SubfolderRef; priceTag?: string } = {}): React.ReactNode => (
    <div key={key} style={{ position: 'relative' }}>
      <button type="button" onClick={onClick}
        style={{
          width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, overflow: 'hidden',
          background: 'rgba(255,255,255,0.04)', color: 'inherit', cursor: 'pointer', padding: 0,
        }}>
        <div style={{ height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)' }}>
          {thumb
            ? <Thumb src={thumb} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover', ...(opts.lock ? { opacity: LOCKED_THUMB_OPACITY } : {}) }} fallback={fallback} />
            : fallback}
        </div>
        <div style={{ padding: '8px 10px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
          {sub && <div style={{ fontSize: 11, opacity: 0.6 }}>{sub}</div>}
        </div>
      </button>
      {opts.lock && <LockBadge />}
      {opts.priceTag && <PriceTag label={opts.priceTag} />}
      {opts.clip && <CoverClip busy={coverBusyId === opts.clip} onClick={() => void setCoverFor(opts.clip!)} />}
      {opts.dl && <DownloadClip busy={downloadBusyId === opts.dl.id} compact={false} color={dlColor} onClick={() => void downloadFolder(opts.dl!)} />}
    </div>
  );

  // ── Compact layout (library-panel-style rows) ──────────────────────────────
  const row = (key: string, label: string, sub: string | undefined, thumb: string | null | undefined, fallback: React.ReactNode, onClick: () => void, opts: { lock?: boolean; clip?: string; dl?: SubfolderRef; priceTag?: string } = {}): React.ReactNode => (
    <div key={key} style={{ position: 'relative' }}>
      <button type="button" onClick={onClick}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
          padding: '6px 8px', paddingRight: opts.dl ? 40 : 8, border: 'none', borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'transparent', color: 'inherit', cursor: 'pointer',
        }}>
        <span style={{ position: 'relative', width: 60, height: 34, borderRadius: 4, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
          {thumb
            ? <Thumb src={thumb} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover', ...(opts.lock ? { opacity: LOCKED_THUMB_OPACITY } : {}) }} fallback={fallback} />
            : fallback}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          {sub && <span style={{ display: 'block', fontSize: 11, opacity: 0.55 }}>{sub}</span>}
        </span>
        {opts.priceTag && <span style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24', whiteSpace: 'nowrap' }}>{opts.priceTag}</span>}
        {opts.lock && !opts.dl && <span style={{ fontSize: 11, opacity: 0.7 }}>🔒</span>}
      </button>
      {opts.clip && <CoverClip busy={coverBusyId === opts.clip} onClick={() => void setCoverFor(opts.clip!)} />}
      {opts.dl && <DownloadClip busy={downloadBusyId === opts.dl.id} compact color={dlColor} onClick={() => void downloadFolder(opts.dl!)} />}
    </div>
  );

  const renderItem = compact ? row : card;
  const folderSub = (f: SubfolderRef) => (f.mediaCount != null ? `${f.mediaCount} item${f.mediaCount === 1 ? '' : 's'}` : undefined);

  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: compact ? 10 : 16, background: 'rgba(255,255,255,0.02)' }}>
      {/* Paywall banner — for-sale folder the viewer hasn't bought yet. */}
      {locked && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)' }}>
          <span style={{ fontSize: 13, color: '#fde68a' }}>🔒 Preview only — unlock to download every file.</span>
          <button type="button" onClick={() => void handleBuy()} disabled={buying}
            style={{ fontSize: 13, fontWeight: 700, color: '#1c1917', background: '#fbbf24', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: buying ? 'wait' : 'pointer', opacity: buying ? 0.7 : 1, whiteSpace: 'nowrap' }}>
            {buying ? 'Starting…' : `Unlock for ${formatPrice(data?.priceCents, data?.currency)}`}
          </button>
        </div>
      )}

      {/* Breadcrumb — capped at the designated root (no ancestors above it). */}
      {data && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 10, fontSize: breadcrumbSize, opacity: 0.85 }}>
          {data.breadcrumb.map((c, i) => (
            <span key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {i > 0 && <span style={{ opacity: 0.4 }}>/</span>}
              <button type="button" onClick={() => setFolderId(c.id)} disabled={c.id === folderId}
                style={{ background: 'none', border: 'none', padding: 0, cursor: c.id === folderId ? 'default' : 'pointer', color: 'inherit', fontWeight: c.id === folderId ? 700 : 400, opacity: c.id === folderId ? 1 : 0.8, textDecoration: c.id === folderId ? 'none' : 'underline' }}>
                {c.name}
              </button>
            </span>
          ))}
        </div>
      )}

      {loading && <div style={{ padding: 24, textAlign: 'center', opacity: 0.6, fontSize: 13 }}>Loading…</div>}
      {err && <div style={{ padding: 24, textAlign: 'center', color: '#fca5a5', fontSize: 13 }}>{err}</div>}

      {data && !loading && !err && (
        <>
          {data.subfolders.length === 0 && data.files.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', opacity: 0.6, fontSize: 13 }}>This folder is empty.</div>
          )}
          <div style={compact ? undefined : gridStyle}>
            {data.subfolders.map((f) =>
              renderItem(f.id, f.name, folderSub(f), f.coverUrl, <FolderIcon size={compact ? 22 : 40} />,
                () => setFolderId(f.id), {
                  clip: editing ? f.id : undefined, dl: f,
                  lock: !!f.paid && !f.owned,
                  priceTag: (f.paid && !f.owned && f.priceCents != null) ? formatPrice(f.priceCents, f.currency) : undefined,
                }))}
            {data.files.map((f) =>
              renderItem(f.id, f.filename, undefined, f.thumbUrl || null,
                fileFallbackIcon(f.mime, compact ? 22 : 40),
                () => onFileClick(f), {
                  lock: (!!f.paid && !f.owned) || locked || !f.url,
                  // Only a file sold on its OWN shows a price chip; folder-covered
                  // files unlock via the folder banner.
                  priceTag: (f.paid && !f.owned && f.salePointKind === 'media' && f.priceCents != null) ? formatPrice(f.priceCents, f.currency) : undefined,
                }))}
          </div>
        </>
      )}

      {active && (
        <FileLightbox
          file={active}
          signedIn={!!user}
          onNeedAuth={() => window.dispatchEvent(new Event('open-signup'))}
          onClose={() => setActive(null)}
        />
      )}
    </div>
  );
};

// Amber "For Sale" price chip on a card's top-left corner.
const PriceTag: React.FC<{ label: string }> = ({ label }) => (
  <span style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(251,191,36,0.92)', color: '#1c1917', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6 }}>{label}</span>
);

const LockBadge: React.FC = () => (
  <span style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.65)', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  </span>
);

// Full-screen overlay previewing a clicked file via the shared media view, with a
// download link to the public url. Clicking the backdrop or ✕ closes it.
const FileLightbox: React.FC<{ file: FileRef; signedIn: boolean; onNeedAuth: () => void; onClose: () => void }> = ({ file, signedIn, onNeedAuth, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const w = Math.min(Math.round(vw * 0.9), 1000);
  const h = Math.round(vh * 0.72);

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.82)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '100%' }}>
        <LibraryMediaView src={file.url || ''} filename={file.filename} mime={file.mime} width={w} height={h} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 10, color: '#e4e4e7' }}>
          <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.filename}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <a href={file.url || '#'} download={file.filename || undefined} target="_blank" rel="noopener noreferrer"
              onClick={(e) => { if (!signedIn) { e.preventDefault(); onNeedAuth(); } }}
              style={{ fontSize: 13, color: '#fff', background: '#2563eb', padding: '6px 14px', borderRadius: 6, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Download
            </a>
            <button type="button" onClick={onClose}
              style={{ fontSize: 13, color: '#e4e4e7', background: 'rgba(255,255,255,0.12)', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer' }}>
              Close
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

export default LibraryFolderBrowser;
